import React, { useContext, useEffect, useReducer, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip
} from "@mui/material";
import { useTheme as useThemeV4 } from "@material-ui/core/styles";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import SmartToyIcon from "@mui/icons-material/SmartToy";

import TableRowSkeleton from "../../components/TableRowSkeleton";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import PromptModal from "../../components/PromptModal";
import { toast } from "react-toastify";
import ConfirmationModal from "../../components/ConfirmationModal";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import ForbiddenPage from "../../components/ForbiddenPage";

const reducer = (state, action) => {
  if (action.type === "LOAD_PROMPTS") {
    const prompts = action.payload;
    const newPrompts = [];

    prompts.forEach((prompt) => {
      const promptIndex = state.findIndex((p) => p.id === prompt.id);
      if (promptIndex !== -1) {
        state[promptIndex] = prompt;
      } else {
        newPrompts.push(prompt);
      }
    });

    return [...state, ...newPrompts];
  }

  if (action.type === "UPDATE_PROMPTS") {
    const prompt = action.payload;
    const promptIndex = state.findIndex((p) => p.id === prompt.id);

    if (promptIndex !== -1) {
      state[promptIndex] = prompt;
      return [...state];
    } else {
      return [prompt, ...state];
    }
  }

  if (action.type === "DELETE_PROMPT") {
    const promptId = action.payload;
    const promptIndex = state.findIndex((p) => p.id === promptId);
    if (promptIndex !== -1) {
      state.splice(promptIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const Prompts = () => {
  const themeV4 = useThemeV4();
  const PRIMARY_MAIN = themeV4?.palette?.primary?.main || "#1976d2";
  const PRIMARY_DARK = themeV4?.palette?.primary?.dark || "#115293";

  const [prompts, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);
  const [promptModalOpen, setPromptModalOpen] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const { user, socket } = useContext(AuthContext);
  const { getPlanCompany } = usePlans();
  const history = useHistory();
  const companyId = user.companyId;

  useEffect(() => {
    async function fetchData() {
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!planConfigs.plan.useOpenAi) {
        toast.error("Esta empresa não possui permissão para acessar essa página! Estamos lhe redirecionando.");
        setTimeout(() => {
          history.push(`/`);
        }, 1000);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/prompt");
        dispatch({ type: "LOAD_PROMPTS", payload: data.prompts });
        setLoading(false);
      } catch (err) {
        toastError(err);
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const onPromptEvent = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_PROMPTS", payload: data.prompt });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_PROMPT", payload: data.promptId });
      }
    };

    socket.on(`company-${companyId}-prompt`, onPromptEvent);
    return () => {
      socket.off(`company-${companyId}-prompt`, onPromptEvent);
    };
  }, [socket, companyId]);

  const handleOpenPromptModal = () => {
    setPromptModalOpen(true);
    setSelectedPrompt(null);
  };

  const handleClosePromptModal = () => {
    setPromptModalOpen(false);
    setSelectedPrompt(null);
  };

  const handleEditPrompt = (prompt) => {
    setSelectedPrompt(prompt);
    setPromptModalOpen(true);
  };

  const handleCloseConfirmationModal = () => {
    setConfirmModalOpen(false);
    setSelectedPrompt(null);
  };

  const handleDeletePrompt = async (promptId) => {
    try {
      const { data } = await api.delete(`/prompt/${promptId}`);
      toast.info(i18n.t(data.message));
    } catch (err) {
      toastError(err);
    }
    setSelectedPrompt(null);
  };

  if (user.profile === "user") {
    return <ForbiddenPage />;
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: { xs: 3, md: 4 },
        px: { xs: 2, md: 3 },
        bgcolor: "#f8fafc"
      }}
    >
      <ConfirmationModal
        title={
          selectedPrompt &&
          `${i18n.t("prompts.confirmationModal.deleteTitle")} ${selectedPrompt.name}?`
        }
        open={confirmModalOpen}
        onClose={handleCloseConfirmationModal}
        onConfirm={() => handleDeletePrompt(selectedPrompt.id)}
      >
        {i18n.t("prompts.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <PromptModal
        open={promptModalOpen}
        onClose={handleClosePromptModal}
        promptId={selectedPrompt?.id}
      />

      <Container maxWidth="xl">
        {/* Header */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={3}
          sx={{ mb: 4 }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              sx={{
                width: 48,
                height: 48,
                borderRadius: 3,
                bgcolor: PRIMARY_MAIN,
                "& svg": { width: 28, height: 28, color: "#fff" }
              }}
            >
              <SmartToyIcon />
            </Avatar>
            <Box>
              <Typography
                variant="h4"
                fontWeight={700}
                sx={{
                  color: "text.primary",
                  mb: 0.5,
                  letterSpacing: "-0.5px",
                  fontSize: { xs: "1.5rem", md: "1.875rem" }
                }}
              >
                {i18n.t("prompts.title")} ({prompts.length})
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500, fontSize: "0.875rem" }}>
                Gerencie os prompts de IA
              </Typography>
            </Box>
          </Stack>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenPromptModal}
            sx={{
              bgcolor: PRIMARY_MAIN,
              color: "#fff",
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 600,
              fontSize: "0.875rem",
              boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
              textTransform: "none",
              "&:hover": {
                bgcolor: PRIMARY_DARK,
                boxShadow: "0 2px 6px rgba(0,0,0,0.18)"
              }
            }}
          >
            {i18n.t("prompts.buttons.add")}
          </Button>
        </Stack>

        {/* Tabela */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            bgcolor: "#fff",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            overflow: "hidden"
          }}
        >
          <Box sx={{ p: 3, borderBottom: "1px solid", borderColor: "divider" }}>
            <Typography variant="h6" fontWeight={700} sx={{ fontSize: "1.125rem" }}>
              Todos os Prompts
            </Typography>
          </Box>

          <TableContainer sx={{ maxHeight: "calc(100vh - 280px)" }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    align="left"
                    sx={{
                      fontWeight: 600,
                      textTransform: "uppercase",
                      fontSize: "0.7rem",
                      color: "text.secondary",
                      letterSpacing: "0.5px",
                      bgcolor: "background.paper"
                    }}
                  >
                    {i18n.t("prompts.table.name")}
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{
                      fontWeight: 600,
                      textTransform: "uppercase",
                      fontSize: "0.7rem",
                      color: "text.secondary",
                      letterSpacing: "0.5px",
                      bgcolor: "background.paper"
                    }}
                  >
                    {i18n.t("prompts.table.queue")}
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{
                      fontWeight: 600,
                      textTransform: "uppercase",
                      fontSize: "0.7rem",
                      color: "text.secondary",
                      letterSpacing: "0.5px",
                      bgcolor: "background.paper"
                    }}
                  >
                    {i18n.t("prompts.table.max_tokens")}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      textTransform: "uppercase",
                      fontSize: "0.7rem",
                      color: "text.secondary",
                      letterSpacing: "0.5px",
                      bgcolor: "background.paper"
                    }}
                  >
                    {i18n.t("prompts.table.actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {prompts.map((prompt) => (
                  <TableRow
                    key={prompt.id}
                    sx={{
                      "&:hover": { bgcolor: "action.hover", transition: "background-color 0.2s" }
                    }}
                  >
                    <TableCell align="left">
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
                        {prompt.name}
                      </Typography>
                    </TableCell>
                    <TableCell align="left">
                      <Chip
                        label={prompt.queue.name}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.75rem",
                          bgcolor: "rgba(99, 102, 241, 0.1)",
                          color: "#6366f1",
                          border: "1px solid rgba(99, 102, 241, 0.2)"
                        }}
                      />
                    </TableCell>
                    <TableCell align="left">
                      <Typography variant="body2" sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
                        {prompt.maxTokens}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <IconButton
                          size="small"
                          onClick={() => handleEditPrompt(prompt)}
                          sx={{
                            color: "text.secondary",
                            "&:hover": { color: PRIMARY_MAIN, bgcolor: "rgba(25, 118, 210, 0.08)" }
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>

                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedPrompt(prompt);
                            setConfirmModalOpen(true);
                          }}
                          sx={{
                            color: "text.secondary",
                            "&:hover": { color: "#ef4444", bgcolor: "rgba(239, 68, 68, 0.08)" }
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {loading && <TableRowSkeleton columns={4} />}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Container>
    </Box>
  );
};

export default Prompts;